import {
  executeTemplateBuilderOperation,
  readWorkingRevision,
  type TemplateBuilderOperation as EditorTemplateBuilderOperation,
  type TemplateOperationContext,
  type TemplateOperationResult,
} from 'adflow';
import { animationAuditFields } from '../animation/audit';
import { animationAvailability, animationUnavailable } from '../animation/availability';
import { runAnimationOperation } from '../animation/mutations';
import { createInvocationId, logTemplateWebMCP } from '../logging';
import {
  isAnimationOperation,
  isTemplateBuilderOperation,
  operationAvailable,
  type TemplateBuilderOperation,
} from '../operations';
import {
  SAVE_CONFIRMATION_INSTRUCTION,
  mcpToolResult,
  toolError,
  toolSuccess,
  type TemplateWebMCPToolResult,
} from '../responses';
import type { TemplateToolContext } from '../toolContext';

const SINGLE_CALL_OPERATIONS = new Set<string>([
  'set_business_entity',
  'add_data_source',
  'remove_data_source',
  'commit_working_copy',
  'stage_saved_version',
  // History moves are steps of their own: a batch is one undo step, so it cannot hold them.
  'undo',
  'redo',
]);

export const TEMPLATE_BUILDER_APPLY_TOOL = {
  name: 'template_builder_apply',
  description:
    'Change the open Template Builder working copy. Input is { operation, ...args } or { changes: [{ operation, ...args }, ...] } for several element edits in one call. set_business_entity, add_data_source, remove_data_source, undo, and redo stay single calls. insert_element { type: "TEXT" | "VECTOR", shape?: "RECTANGLE" | "ELLIPSE" | "TRIANGLE", x, y, width?, height?, content?, name? } adds a top-most layer with the editor defaults and returns elementId. insert_element, add_element and remove_element are undoable with undo and redo, as are drags on the canvas, and one changes call is one undo step. set_business_entity loads the advertiser fonts when no shadow brand is set; read brand again before choosing a font. Returns { ok, data?, error? }. Success data includes operation and workingRevision. A changes call returns operation "changes" and applied. If one step fails, the working copy is restored and error details include index and operation. update_element_data merges into the current element data; omitted fields stay and null clears a field. Pick fontFamily and fontVariant from brand fonts variants; do not guess labels such as regular. When animation is enabled, apply_animation_effect { targetId, presetId, params? }, update_animation_effect { effectId, params?, presetId? } and remove_animation_effect { effectId } edit preset effects; timing is in seconds and distance in pixels; pass expectedWorkingRevision to reject a stale write; success data includes effect, replaced, and warnings; read capability topic animation first. Otherwise they return CAPABILITY_UNAVAILABLE. reorder_element { elementId, command } sets stacking order to front, forward, backward, or back. The Figma frame SVG and root background vector cannot be reordered. group_elements returns UNSUPPORTED and does not write. commit_working_copy is two steps. The first call opens Confirm Save and returns an instruction to wait; it does not write. Call it again with userConfirmed true only when the user explicitly asked to save, or with cancel true to close the dialog without saving. Args: { expectedWorkingRevision, userConfirmed?, cancel? }. Apply it on its own. Cancel returns saved false. A blocking warning returns INVALID_INPUT. A revision mismatch returns STALE_REVISION. Error code is UNAUTHORIZED, INVALID_INPUT, EDITOR_NOT_READY, STALE_REVISION, UNSUPPORTED, CAPABILITY_UNAVAILABLE, ANIMATION_PRESET_UNKNOWN, ANIMATION_EFFECT_NOT_FOUND, ANIMATION_TARGET_INVALID, ANIMATION_DUPLICATE, ANIMATION_CONFLICT, ANIMATION_ROLE_MISMATCH, ANIMATION_PARAMS_INVALID or UNEXPECTED.',
  enabled: true,
  annotations: {
    title: 'Apply Template Builder',
    readOnlyHint: false,
    destructiveHint: false,
    idempotentHint: false,
    openWorldHint: false,
  },
  inputSchema: {
    type: 'object',
    properties: {
      operation: { type: 'string' },
      expectedWorkingRevision: { type: 'number' },
      userConfirmed: {
        type: 'boolean',
        description:
          'Set true only when the user explicitly asked to save. Omit it to open Confirm Save and wait.',
      },
      cancel: {
        type: 'boolean',
        description:
          'Set true to close Confirm Save without saving. Do not combine with userConfirmed.',
      },
    },
    required: ['operation'],
    additionalProperties: true,
  } as const,
  outputSchema: {
    type: 'object',
    properties: {
      ok: { type: 'boolean' },
      data: { type: 'object' },
      error: {
        type: 'object',
        properties: { code: { type: 'string' }, message: { type: 'string' } },
        required: ['code', 'message'],
      },
    },
    required: ['ok'],
  } as const,
};

type ApplyData = {
  operation: string;
  workingRevision: number;
  applied?: number;
  saved?: boolean;
  instruction?: string;
  [animationField: string]: unknown;
};

const DOCUMENT_SNAPSHOT_FIELDS = [
  'elements',
  'name',
  'bgColour',
  'dataSources',
  'displayOptions',
  'brand',
  'brandKit',
  'brandKitId',
  'importSourceJSON',
  'importSourceType',
  'customFonts',
  'businessEntityId',
  'selectedElementId',
  'selectedElementIds',
  'animation',
  'undoStack',
  'redoStack',
  'pendingTextElementId',
  'workingRevision',
] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function toOperationContext(context: TemplateToolContext): TemplateOperationContext {
  return {
    source: 'webmcp',
    store: context.store,
    signal: context.signal,
    routeMode: context.routeMode,
    templateId: context.templateId,
    toolName: TEMPLATE_BUILDER_APPLY_TOOL.name,
    onOperationComplete: context.onOperationComplete,
    listAdvertisers: context.listAdvertisers,
    loadAdvertiserFonts: context.loadAdvertiserFonts,
    redeemSavedVersionRestore: context.redeemSavedVersion
      ? (args) => context.redeemSavedVersion!(args)
      : undefined,
  };
}

function mapOperationResultToMcp(
  result: TemplateOperationResult,
): TemplateWebMCPToolResult<ApplyData> {
  if (result.ok) {
    const data: ApplyData = {
      operation: result.data.operation,
      workingRevision: result.data.workingRevision,
      applied: result.data.applied,
      saved: result.data.saved,
    };
    for (const field of [
      'elementId',
      'name',
      'type',
      'position',
      'size',
      'changed',
      'undoDepth',
      'redoDepth',
    ] as const) {
      if (result.data[field] !== undefined) {
        data[field] = result.data[field];
      }
    }
    if (result.data.settlementStatus === 'awaiting_confirmation') {
      data.instruction = SAVE_CONFIRMATION_INSTRUCTION;
    }
    return mcpToolResult(toolSuccess(data));
  }
  const { code, message, details } = result.error;
  return mcpToolResult(toolError(code, message, details));
}

async function runOperation(
  context: TemplateToolContext,
  operation: TemplateBuilderOperation,
  input: Record<string, unknown>,
  invocationId?: string,
): Promise<TemplateWebMCPToolResult<ApplyData>> {
  if (isAnimationOperation(operation)) {
    if (!operationAvailable(operation, animationAvailability(context))) {
      return mcpToolResult(animationUnavailable());
    }
    if (!context.store) {
      return mcpToolResult(
        toolError('EDITOR_NOT_READY', 'The Template Builder editor is not ready.'),
      );
    }
    return runAnimationOperation(context.store, operation, input);
  }

  const result = await executeTemplateBuilderOperation(
    toOperationContext(context),
    { operation: operation as EditorTemplateBuilderOperation, args: input },
    invocationId,
  );
  return mapOperationResultToMcp(result);
}

function snapshotDocument(state: Record<string, unknown>): Record<string, unknown> {
  const snapshot: Record<string, unknown> = {};
  for (const field of DOCUMENT_SNAPSHOT_FIELDS) {
    snapshot[field] = state[field];
  }
  return structuredClone(snapshot);
}

function stepFailure(
  result: TemplateWebMCPToolResult<ApplyData>,
  index: number,
  operation: string,
): TemplateWebMCPToolResult<ApplyData> {
  if (result.structuredContent.ok) {
    return result;
  }
  const { code, message, details } = result.structuredContent.error;
  const prior = isRecord(details) ? details : {};
  const restored = message.includes('working copy was restored')
    ? message
    : `${message} The working copy was restored.`;
  return mcpToolResult(toolError(code, restored, { ...prior, index, operation }));
}

function parseChanges(
  input: Record<string, unknown>,
):
  | { changes: Array<Record<string, unknown> & { operation: TemplateBuilderOperation }> }
  | { error: string } {
  if (!Array.isArray(input.changes) || input.changes.length === 0) {
    return { error: 'changes must be a non-empty array.' };
  }
  const changes: Array<Record<string, unknown> & { operation: TemplateBuilderOperation }> = [];
  for (const [index, step] of input.changes.entries()) {
    if (!isRecord(step) || !isTemplateBuilderOperation(String(step.operation ?? ''))) {
      return { error: `changes[${index}] requires a known operation.` };
    }
    if (SINGLE_CALL_OPERATIONS.has(String(step.operation))) {
      return {
        error: `changes cannot include ${step.operation}. Apply it on its own.`,
      };
    }
    changes.push(step as Record<string, unknown> & { operation: TemplateBuilderOperation });
  }
  return { changes };
}

export async function executeTemplateBuilderApply(
  context: TemplateToolContext,
): Promise<TemplateWebMCPToolResult<ApplyData>> {
  const invocationId = createInvocationId();
  const startedAt = Date.now();
  const input = isRecord(context.input) ? context.input : undefined;
  const operation =
    typeof input?.operation === 'string'
      ? input.operation
      : input && 'changes' in input
      ? 'changes'
      : undefined;
  logTemplateWebMCP('invocation_start', {
    tool: TEMPLATE_BUILDER_APPLY_TOOL.name,
    invocationId,
    operation,
  });

  const finish = (
    result: TemplateWebMCPToolResult<ApplyData>,
    outcome: 'success' | 'validation_failure' | 'cancellation' | 'unexpected_failure',
  ) => {
    logTemplateWebMCP('invocation_complete', {
      tool: TEMPLATE_BUILDER_APPLY_TOOL.name,
      invocationId,
      operation,
      outcome,
      durationMs: Date.now() - startedAt,
      workingRevision: result.structuredContent.ok
        ? result.structuredContent.data.workingRevision
        : undefined,
      errorCode: result.structuredContent.ok ? undefined : result.structuredContent.error.code,
      ...(input && isAnimationOperation(String(operation))
        ? animationAuditFields(input, result)
        : {}),
    });
    return result;
  };

  try {
    if (context.signal?.aborted) {
      return finish(
        mcpToolResult(toolError('UNEXPECTED', 'The tool invocation was cancelled.')),
        'cancellation',
      );
    }
    if (!context.canEdit) {
      return finish(
        mcpToolResult(toolError('UNAUTHORIZED', 'Template Builder edit permission is required.')),
        'validation_failure',
      );
    }
    if (!input) {
      return finish(
        mcpToolResult(
          toolError('INVALID_INPUT', 'template_builder_apply requires a known operation.'),
        ),
        'validation_failure',
      );
    }
    if ('changes' in input) {
      if ('operation' in input) {
        return finish(
          mcpToolResult(
            toolError(
              'INVALID_INPUT',
              'template_builder_apply accepts operation or changes, not both.',
            ),
          ),
          'validation_failure',
        );
      }
      const parsed = parseChanges(input);
      if ('error' in parsed) {
        return finish(
          mcpToolResult(toolError('INVALID_INPUT', parsed.error)),
          'validation_failure',
        );
      }
      if (!context.store || typeof context.store.setState !== 'function') {
        return finish(
          mcpToolResult(
            toolError(
              'INVALID_INPUT',
              'A batch apply needs a store that can restore the working copy.',
            ),
          ),
          'validation_failure',
        );
      }
      const current = context.store.getState();
      if (!current || !Array.isArray(current.elements)) {
        return finish(
          mcpToolResult(toolError('EDITOR_NOT_READY', 'The Template Builder editor is not ready.')),
          'validation_failure',
        );
      }
      const snapshot = snapshotDocument(current);
      // Everything in one `changes` call is a single undo step, or none when a step fails.
      const history = current as {
        beginHistoryBatch?: () => void;
        endHistoryBatch?: (commit: boolean) => void;
      };
      history.beginHistoryBatch?.();
      const restore = () => {
        history.endHistoryBatch?.(false);
        context.store?.setState?.(snapshot);
      };
      for (const [index, step] of parsed.changes.entries()) {
        try {
          const result = await runOperation(context, step.operation, step);
          if (!result.structuredContent.ok) {
            restore();
            return finish(stepFailure(result, index, step.operation), 'validation_failure');
          }
        } catch (error) {
          restore();
          return finish(
            mcpToolResult(
              toolError(
                'UNEXPECTED',
                'An unexpected error occurred. The working copy was restored.',
                {
                  index,
                  operation: step.operation,
                  message: error instanceof Error ? error.message : String(error),
                },
              ),
            ),
            'unexpected_failure',
          );
        }
      }
      history.endHistoryBatch?.(true);
      return finish(
        mcpToolResult(
          toolSuccess({
            operation: 'changes',
            applied: parsed.changes.length,
            workingRevision: readWorkingRevision(context.store.getState()),
          }),
        ),
        'success',
      );
    }
    if (!isTemplateBuilderOperation(String(input.operation ?? ''))) {
      return finish(
        mcpToolResult(
          toolError('INVALID_INPUT', 'template_builder_apply requires a known operation.'),
        ),
        'validation_failure',
      );
    }
    const result = await runOperation(context, input.operation as TemplateBuilderOperation, input);
    if (context.signal?.aborted) {
      return finish(
        mcpToolResult(toolError('UNEXPECTED', 'The tool invocation was cancelled.')),
        'cancellation',
      );
    }
    const outcome = result.structuredContent.ok
      ? 'success'
      : result.structuredContent.error.code === 'UNEXPECTED'
      ? 'unexpected_failure'
      : 'validation_failure';
    return finish(result, outcome);
  } catch (error) {
    return finish(
      mcpToolResult(
        toolError('UNEXPECTED', 'An unexpected error occurred.', {
          message: error instanceof Error ? error.message : String(error),
        }),
      ),
      'unexpected_failure',
    );
  }
}
